import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Container } from "@/components/ui-kit";
import { fetchCustomerOrder } from "@/lib/api";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";

// §8 of the payments spec, restated: this page is a landing screen only. It never
// activates an order/entitlement itself — that only ever happens from the verified
// PayMongo webhook (see billing_routes.py's _process_paymongo_event). A customer
// simply reaching this URL proves nothing about whether payment actually succeeded.
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { token, checking, isLoggedIn } = useCustomerAuth();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!token || !orderId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await fetchCustomerOrder(token, orderId);
        if (cancelled) return;
        setStatus(next);
        if (next.status === "active" && next.website_url) window.location.replace(next.website_url);
      } catch { /* keep a truthful pending screen */ }
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [token, orderId]);
  if (checking) return null;
  if (!isLoggedIn) return <Navigate to={`/account/login?return_to=${encodeURIComponent(`/billing/success?order_id=${orderId || ""}`)}`} replace />;
  return (
    <div className="min-h-[70vh] bg-[#F8FAFC] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-8 text-center shadow-sm">
        <CheckCircle2 size={40} className="mx-auto text-[#10B981]" />
        <h1 className="mt-4 text-xl font-bold font-display text-[#0F172A]">Thanks — we're confirming your payment</h1>
        <p className="mt-3 text-sm text-[#475569] leading-relaxed">
          This can take a few moments. Your website's billing status will update automatically once payment is
          confirmed — no action is needed from you.
        </p>
        {orderId && <p className="mt-3 text-xs text-[#94A3B8]">Reference: {orderId} · Status: {status?.status || "confirming"}</p>}
        <Link to="/account/dashboard" className="mt-6 inline-flex items-center justify-center rounded-xl bg-[#0F172A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1E293B] transition-colors">
          Go to my dashboard
        </Link>
      </div>
    </div>
  );
}
